<template>
  <div class="product-detail-page">
    <AppHeader />

    <div class="detail-container">
      <el-button type="text" @click="goBack" class="back-btn">
        <el-icon><ArrowLeft /></el-icon> 返回产品列表
      </el-button>

      <div v-if="loading" class="loading-wrapper">
        <el-skeleton :rows="8" animated />
      </div>

      <el-card v-else-if="product" class="detail-card" shadow="hover">
        <el-row :gutter="40">
          <el-col :xs="24" :md="12">
            <div class="detail-image-wrapper">
              <el-image 
                :src="product.image" 
                fit="cover"
                class="detail-image"
              >
                <template #placeholder>
                  <div class="image-placeholder">
                    <el-icon :size="60"><Monitor /></el-icon>
                  </div>
                </template>
              </el-image>
              <div class="product-tag" v-if="product.tag">
                <el-tag :type="product.tagType" size="large" effect="dark" round>
                  {{ product.tag }}
                </el-tag>
              </div>
            </div>
          </el-col>

          <el-col :xs="24" :md="12">
            <div class="detail-info">
              <div class="category-tag">
                <el-tag size="small" round>{{ categoryMap[product.category] || product.category }}</el-tag>
              </div>

              <h1>{{ product.name }}</h1>
              <p class="detail-desc">{{ product.desc }}</p>

              <div class="spec-section" v-if="product.specs && product.specs.length">
                <h3>规格参数</h3>
                <div class="spec-grid">
                  <div v-for="(spec, index) in product.specs" :key="index" class="spec-item">
                    <span class="spec-label">{{ spec.label }}</span>
                    <span class="spec-value">{{ spec.value }}</span>
                  </div>
                </div>
              </div>

              <div class="detail-features" v-if="product.features && product.features.length">
                <div v-for="(feature, index) in product.features" :key="index" class="feature-item">
                  <el-icon><CircleCheck /></el-icon>
                  <span>{{ feature }}</span>
                </div>
              </div>

              <div class="detail-price">
                <span class="price-label">参考价格</span>
                <span class="price-value">¥{{ product.price ? product.price.toFixed(2) : '面议' }}</span>
                <span class="price-unit" v-if="product.priceUnit">{{ product.priceUnit }}</span>
              </div>

              <div class="detail-actions">
                <el-button type="primary" size="large" round @click="handlePurchase">
                  <el-icon><ShoppingCart /></el-icon> 跳转店铺
                </el-button>
                <el-button size="large" round @click="handleContact">
                  <el-icon><Service /></el-icon> 咨询客服
                </el-button>
              </div>

              <div class="stock-status" v-if="product.stock !== undefined">
                <span class="status-dot" :class="product.stock > 0 ? 'in-stock' : 'out-stock'"></span>
                <span class="status-text">{{ product.stock > 0 ? `库存 ${product.stock} 件` : '暂无库存' }}</span>
              </div>
            </div>
          </el-col>
        </el-row>

        <!-- ========== 产品详情长图（淘宝风格） ========== -->
        <div class="detail-long-images">
          <h3>产品详情</h3>
          <div v-if="sortedFiles.length" class="long-images-container">
            <el-image
              v-for="(file, index) in sortedFiles"
              :key="file.id"
              :src="file.filePath"
              fit="fill"
              class="long-image"
              :preview-src-list="sortedFiles.map(f => f.filePath)"
              :initial-index="index"
              hide-on-click-modal
            />
          </div>
          <div v-else class="no-images-tip">
            <el-icon><Picture /></el-icon>
            <span>暂无产品详情图片</span>
          </div>
        </div>

        <!-- 相关产品推荐 -->
        <div class="related-products" v-if="relatedProducts.length">
          <h3>相关产品推荐</h3>
          <el-row :gutter="16">
            <el-col :xs="12" :sm="8" :md="6" v-for="item in relatedProducts" :key="item.id">
              <div class="related-card" @click="goToDetail(item.id)">
                <el-image :src="item.image" fit="cover" class="related-image">
                  <template #placeholder>
                    <div class="related-placeholder"><el-icon><Monitor /></el-icon></div>
                  </template>
                </el-image>
                <p class="related-name">{{ item.name }}</p>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-card>

      <el-empty v-else description="产品不存在或已被删除" />
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { ElMessage } from 'element-plus'
import { 
  ArrowLeft, Monitor, CircleCheck, ShoppingCart, 
  Service, Document, Picture 
} from '@element-plus/icons-vue'
import AppHeader from '@/components/AppHeader.vue'
import { getProductById, getRelatedProducts, getCategories } from '@/api/product'

const router = useRouter()
const route = useRoute()

// ========== 状态 ==========
const product = ref(null)
const relatedProducts = ref([])
const loading = ref(true)
const categoryMap = ref({})

// ========== 计算属性 ==========
// 按 sortOrder 降序排列的文件列表（值越大越靠前）
const sortedFiles = computed(() => {
  const files = product.value?.files || []
  return [...files].sort((a, b) => (b.sortOrder || 0) - (a.sortOrder || 0))
})

// ========== 加载分类映射 ==========
const loadCategoryMap = async () => {
  try {
    const res = await getCategories()
    const list = res.data || []
    const map = {}
    list.forEach(item => {
      map[item.code] = item.name
    })
    categoryMap.value = map
  } catch (error) {
    console.error('加载分类失败:', error)
    categoryMap.value = {
      router: '工业路由器',
      dtu: '数传DTU',
      module: '通信模组',
      gateway: '边缘网关',
      software: '软件服务',
      switch: '工业交换机',
      ap: '无线AP'
    }
  }
}

// ========== 获取产品详情 ==========
const fetchProductDetail = async (id) => {
  if (!id) {
    loading.value = false
    return
  }

  loading.value = true
  try {
    const res = await getProductById(id)
    const data = res.data || res

    product.value = {
      id: data.id,
      name: data.name,
      model: data.model,
      category: data.category || data.categoryCode,
      image: data.image,
      desc: data.description || '',
      description: data.content || '',
      price: data.price,
      priceUnit: data.priceUnit || '元',
      stock: data.stock,
      tag: data.tag,
      specs: (data.specs || []).map(s => ({
        label: s.specKey,
        value: s.specValue
      })),
      features: (data.features || []).map(f => f.description),
      dataSheet: data.pdfUrl || null,
      tagType: data.tag === 'hot' ? 'danger' : data.tag === 'new' ? 'primary' : 'warning',
      files: data.files || []   // 保留原始文件列表
    }

    if (data.categoryId) {
      const relatedRes = await getRelatedProducts({
        categoryId: data.categoryId,
        excludeId: id,
        limit: 4
      })
      const relatedList = relatedRes.data || []
      relatedProducts.value = relatedList.map(item => ({
        id: item.id,
        name: item.name,
        image: item.image
      }))
    }

  } catch (error) {
    console.error('获取产品详情失败:', error)
    ElMessage.error(error.message || '加载产品详情失败，请稍后重试')
    product.value = null
  } finally {
    loading.value = false
  }
}

// ========== 方法 ==========
const goBack = () => {
  router.push('/products')
}

const goToDetail = (id) => {
  if (id) {
    router.push(`/product/${id}`)
  }
}

const handlePurchase = () => {
  ElMessage.success(`店铺功能正在开发中，如需购买请使用邮箱联系`)
}

const handleContact = () => {
  ElMessage.info('客服功能开发中，请稍后...')
}

const handleDownload = () => {
  router.push(`/wikis`)
}

// ========== 监听路由变化 ==========
watch(
  () => route.params.id,
  (newId) => {
    if (newId) {
      fetchProductDetail(newId)
    }
  }
)

// ========== 生命周期 ==========
onMounted(async () => {
  await loadCategoryMap()
  const id = route.params.id
  if (id) {
    fetchProductDetail(id)
  }
})
</script>

<style scoped>
.product-detail-page {
  padding-top: 72px;
  min-height: 100vh;
  background: #f8fafc;
}

.detail-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px;
}

.back-btn {
  margin-bottom: 20px;
  font-size: 15px;
  color: #4b5563;
}

.back-btn:hover {
  color: #4F46E5;
}

.loading-wrapper {
  padding: 40px 0;
}

.detail-card {
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
}

/* ===== 产品图片 ===== */
.detail-image-wrapper {
  position: relative;
  width: 100%;
  min-height: 360px;
  border-radius: 16px;
  overflow: hidden;
  background: #f1f5f9;
}

.detail-image {
  width: 100%;
  height: 400px;
  display: block;
}

.image-placeholder {
  width: 100%;
  height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: #e5e7eb;
  gap: 12px;
}

.product-tag {
  position: absolute;
  top: 16px;
  left: 16px;
  z-index: 2;
}

/* ===== 产品信息 ===== */
.detail-info {
  padding: 8px 0;
}

.category-tag {
  margin-bottom: 12px;
}

.detail-info h1 {
  font-size: 28px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.detail-desc {
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 20px;
}

/* ===== 规格参数 ===== */
.spec-section {
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
}

.spec-section h3 {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 12px 0;
}

.spec-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px 16px;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  border-bottom: 1px dashed #e8edf2;
}

.spec-item:last-child,
.spec-item:nth-last-child(2) {
  border-bottom: none;
}

.spec-label {
  color: #6b7280;
  font-size: 13px;
}

.spec-value {
  color: #1a1a2e;
  font-size: 13px;
  font-weight: 500;
}

/* ===== 产品特性 ===== */
.detail-features {
  margin-bottom: 20px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 0;
  color: #374151;
  font-size: 14px;
}

.feature-item .el-icon {
  color: #10b981;
  font-size: 18px;
  flex-shrink: 0;
}

/* ===== 价格和操作 ===== */
.detail-price {
  display: flex;
  align-items: baseline;
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid #f1f5f9;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 20px;
}

.price-label {
  color: #6b7280;
  font-size: 14px;
}

.price-value {
  font-size: 28px;
  font-weight: 700;
  color: #ef4444;
}

.price-unit {
  color: #6b7280;
  font-size: 13px;
}

.detail-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.detail-actions .el-button {
  padding: 12px 28px;
  font-weight: 600;
}

.stock-status {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.status-dot.in-stock {
  background: #10b981;
}

.status-dot.out-stock {
  background: #ef4444;
}

.status-text {
  color: #6b7280;
}

/* ===== 产品详情长图 ===== */
.detail-long-images {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
}

.detail-long-images h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 16px 0;
}

.long-images-container {
  display: flex;
  flex-direction: column;
}

/* 1:1 方形容器；fit="fill" 以内联样式作用在内部 img 上，图片强制拉伸铺满、无留白 */
.long-image {
  width: 100%;
  aspect-ratio: 1 / 1;
  display: block;
  overflow: hidden;
}

.no-images-tip {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  color: #9ca3af;
  gap: 12px;
}

.no-images-tip .el-icon {
  font-size: 48px;
}

/* ===== 相关产品推荐 ===== */
.related-products {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #f1f5f9;
}

.related-products h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 16px 0;
}

.related-card {
  cursor: pointer;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;
  background: #fff;
}

.related-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  border-color: #4F46E5;
}

.related-image {
  width: 100%;
  height: 140px;
  display: block;
  background: #f1f5f9;
}

.related-placeholder {
  width: 100%;
  height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  background: #e5e7eb;
}

.related-name {
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 500;
  color: #1a1a2e;
  text-align: center;
  margin: 0;
  line-height: 1.4;
}

/* ===== 响应式 ===== */
@media (max-width: 768px) {
  .detail-container {
    padding: 16px;
  }

  .detail-image-wrapper {
    min-height: 200px;
  }

  .detail-image {
    height: 250px;
  }

  .image-placeholder {
    height: 250px;
  }

  .detail-info h1 {
    font-size: 22px;
  }

  .spec-grid {
    grid-template-columns: 1fr;
  }

  .spec-item:last-child {
    border-bottom: none;
  }

  .price-value {
    font-size: 22px;
  }

  .detail-actions .el-button {
    padding: 10px 20px;
    font-size: 14px;
  }

  .related-image {
    height: 100px;
  }

  .related-placeholder {
    height: 100px;
  }
}

@media (max-width: 480px) {
  .detail-container {
    padding: 12px;
  }

  .detail-image {
    height: 200px;
  }

  .image-placeholder {
    height: 200px;
  }

  .detail-info h1 {
    font-size: 18px;
  }

  .detail-actions {
    flex-direction: column;
  }

  .detail-actions .el-button {
    width: 100%;
    justify-content: center;
  }
}
</style>